import React, { useState } from "react";
import { Link } from "react-router-dom";
import { FaMapMarkerAlt, FaClock } from "react-icons/fa";
import { FiArrowLeft, FiLayers } from "react-icons/fi";
import MediaGallery from "../../components/media/MediaGallery";
import DOMPurify from "dompurify";
import TimeAgo from "../../components/timeAgo/TimeAgo";
import styles from "./PostDetailCard.module.css";

const PostDetailCard = ({
  creatorName = "User",
  description = "",
  createdOn,
  state,
  city,
  images = [],
  videos = [],
  attachments = [],
  service,
  photo,
  back = "/customer/posts",
}) => {
  const [showFullDescription, setShowFullDescription] = useState(false);
  const descriptionWords = description?.trim().split(/\s+/) || [];
  const isLongDescription = descriptionWords.length > 60;

  const displayedDescription =
    isLongDescription && !showFullDescription
      ? descriptionWords.slice(0, 60).join(" ") + "…"
      : description;

  const rawAssetUrl =
    process.env.REACT_APP_ASSET_URL || process.env.REACT_APP_BASE_URL || "";
  const cleanAssetUrl = rawAssetUrl ? rawAssetUrl.replace(/\/+$/, "") : "";

  const fullPhotoSrc = photo
    ? photo.startsWith("http://") || photo.startsWith("https://")
      ? photo
      : `${cleanAssetUrl}${photo.startsWith("/") ? "" : "/"}${photo}`
    : null;

  const initials = (creatorName || "User")
    .split(" ")
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase())
    .join("");

  return (
    <div className={styles.container}>
      <div className={styles.card}>
        {/* Media Gallery */}
        {(images.length > 0 || videos.length > 0 || attachments.length > 0) && (
          <div className={styles.mediaSection}>
            <MediaGallery
              images={images}
              promotion_videos={videos}
              attachments={attachments}
            />
          </div>
        )}

        {/* Author Header */}
        <div className={styles.authorRow}>
          {fullPhotoSrc ? (
            <img
              src={fullPhotoSrc}
              alt={creatorName}
              className={styles.authorAvatar}
              onError={(e) => {
                e.target.style.display = "none";
                e.target.nextSibling.style.display = "flex";
              }}
            />
          ) : null}
          <div
            className={styles.authorInitials}
            style={{ display: fullPhotoSrc ? "none" : "flex" }}
          >
            {initials || "US"}
          </div>
          <div className={styles.authorInfo}>
            <h2 className={styles.authorName}>{creatorName}</h2>
          </div>
        </div>

        {/* Meta Chips */}
        <div className={styles.metaRow}>
          {createdOn && (
            <span className={styles.metaChip}>
              <FaClock className="text-primary" />
              <TimeAgo timestamp={createdOn} />
            </span>
          )}

          {(city || state) && (
            <span className={styles.metaChip}>
              <FaMapMarkerAlt className="text-danger" />
              {[city, state].filter(Boolean).join(", ")}
            </span>
          )}

          {service && (
            <span className={styles.serviceBadge}>
              <FiLayers size={14} />
              {service}
            </span>
          )}
        </div>

        {/* Description */}
        <div className={styles.descriptionSection}>
          <div className={styles.descriptionTitle}>Post Details</div>
          <div
            className={styles.descriptionBody}
            dangerouslySetInnerHTML={{
              __html: DOMPurify.sanitize(displayedDescription),
            }}
          />
          {isLongDescription && (
            <button
              type="button"
              className="btn btn-link p-0 mt-2 text-decoration-none fw-semibold"
              onClick={() => setShowFullDescription(!showFullDescription)}
            >
              {showFullDescription ? "Show Less" : "Read Full Post"}
            </button>
          )}
        </div>

        {/* Back Button Action */}
        <div className="mt-4 pt-2">
          <Link to={back} className={styles.btnBack}>
            <FiArrowLeft size={16} />
            Back
          </Link>
        </div>
      </div>
    </div>
  );
};

export default PostDetailCard;
